import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

/* ─── Status pill ─────────────────────────────────────────────────── */
const STATUS = {
  cancelled: { bg: '#FEF2F2', border: '#FECACA', color: '#DC2626' },
  confirmed:  { bg: '#F0FDF4', border: '#BBF7D0', color: '#16A34A' },
  draft:      { bg: '#F8FAFC', border: '#E2E8F0', color: '#64748B' },
  pending:    { bg: '#FFFBEB', border: '#FDE68A', color: '#D97706' },
};
const getStatus = (s = '') => STATUS[s.toLowerCase()] || STATUS.draft;

const StatusPill = ({ status, label }) => {
  const s = getStatus(status);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      px: 1.25, py: 0.5, borderRadius: '20px',
      bgcolor: s.bg, border: `0.5px solid ${s.border}`,
    }}>
      <Typography sx={{ fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 500, color: s.color, textTransform: 'capitalize' }}>
        {label}
      </Typography>
    </Box>
  );
};

/* ─── Field ───────────────────────────────────────────────────────── */
const Field = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', letterSpacing: '0.04em', mb: '3px' }}>
      {label}
    </Typography>
    {children}
  </Box>
);

/* ─── Main component ──────────────────────────────────────────────── */
const LeadBookings = ({ bookings = [] }) => {
  if (!bookings.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px',
          border: '0.5px solid', borderColor: 'divider',
          bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <BookmarkBorderIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Box>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.primary' }}>No bookings yet</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          Bookings for this lead will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: '28px 32px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
          Bookings
        </Typography>
        <Box sx={{
          px: 1.25, py: 0.4, borderRadius: '20px',
          bgcolor: 'grey.50', border: '0.5px solid', borderColor: 'divider',
        }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
            {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
        {bookings.map((booking) => {
          const amt = (v) => `₹${v?.toLocaleString('en-IN') ?? '0'}`;
          const hasAmount = (v) => (v ?? 0) > 0;

          return (
            <Box
              key={booking.id}
              sx={{
                bgcolor: 'background.paper',
                border: '0.5px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.1s',
                boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
                '&:hover': {
                  borderColor: 'grey.300',
                  boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {/* Card header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '8px',
                    bgcolor: 'grey.50', border: '0.5px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  </Box>
                  <Box>
                    <Link
                      component={RouterLink}
                      to={`/bookings/${booking.id}`}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {booking.booking_id}
                    </Link>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', mt: '2px' }}>
                      {booking.booking_date
                        ? new Date(booking.booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Date not set'}
                    </Typography>
                  </Box>
                </Box>
                <StatusPill status={booking.status} label={booking.status_verbose || booking.status} />
              </Box>

              {/* Divider */}
              <Box sx={{ height: '0.5px', bgcolor: 'divider' }} />

              {/* Card body — 4 fields */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5, px: 2, py: 1.75 }}>
                <Field label="Booking amount">
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: hasAmount(booking.booking_amount) ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {amt(booking.booking_amount)}
                  </Typography>
                </Field>

                <Field label="Token amount">
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: hasAmount(booking.token_amount) ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {amt(booking.token_amount)}
                  </Typography>
                </Field>

                <Field label="Token paid on">
                  {booking.token_paid_on_str ? (
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
                      {booking.token_paid_on_str}
                    </Typography>
                  ) : (
                    <Box sx={{
                      display: 'inline-flex', px: 1, py: '2px',
                      bgcolor: 'grey.50', border: '0.5px solid', borderColor: 'divider',
                      borderRadius: '6px',
                    }}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: 'text.secondary' }}>Not paid</Typography>
                    </Box>
                  )}
                </Field>

                <Field label="Unit">
                  {booking.unit?.configuration_id ? (
                    <Link
                      component={RouterLink}
                      to={`/projects/${booking.project?.id || booking.unit?.project_id}#configurations/${booking.unit.configuration_id}/units`}
                      sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0D9488', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
                        {booking.unit.unit_number || 'N/A'}
                      </Typography>
                    </Link>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.300', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        {booking.project
                          ? `${booking.project.name} · no unit`
                          : booking.status === 'draft'
                          ? 'Draft — unassigned'
                          : 'No unit'}
                      </Typography>
                    </Box>
                  )}
                </Field>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default LeadBookings;
